import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import yaml from 'js-yaml';
import { api, getErrorMessage } from '../lib/api';
import { toast } from '../utils/toast';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useAuthStore } from '../stores/authStore';
import { ApiDocumentation } from '../components/documentation/ApiDocumentation';
import { ApiTester } from '../features/api-tester';
import { LoadingState } from '../components/ui/LoadingState';
import { EmptyState } from '../components/ui/EmptyState';
import type { Product, Plan, Application, Subscription } from '../types';
import {
  ClipboardList,
  DollarSign,
  BookOpen,
  CheckCircle2,
  ArrowLeft,
  Download,
  Loader2,
  Cpu,
  Layers,
  KeyRound,
  ShieldCheck,
  Calendar,
  Check,
  History,
  FileCode,
  Terminal,
  FlaskConical,
  Calculator,
} from 'lucide-react';
import { PricingQuotaSimulator } from '../components/PricingQuotaSimulator';

interface ProductWithPlans extends Product {
  plans: Plan[];
}
interface ContextType {
  application: Application;
}

const steps = [
  { id: 'overview', label: 'Visão Geral', icon: <ClipboardList className="w-4 h-4" /> },
  { id: 'documentation', label: 'Documentação OpenAPI', icon: <BookOpen className="w-4 h-4" /> },
  { id: 'tester', label: 'Consola de Testes Sandbox', icon: <FlaskConical className="w-4 h-4" /> },
  { id: 'plans', label: 'Planos & Subscrição', icon: <DollarSign className="w-4 h-4" /> },
  { id: 'changelog', label: 'Versões & Changelog', icon: <History className="w-4 h-4" /> },
];

export const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { environment } = useEnvironment();
  const client = useAuthStore((state) => state.client);
  const { application } = useOutletContext<ContextType>();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(
    tabParam &&
      ['overview', 'plans', 'documentation', 'tester', 'changelog'].includes(tabParam)
      ? tabParam
      : 'overview'
  );
  const [showSimulator, setShowSimulator] = useState(false);

  useEffect(() => {
    const currentTab = searchParams.get('tab');
    if (
      currentTab &&
      ['overview', 'plans', 'documentation', 'tester', 'changelog'].includes(currentTab)
    ) {
      setActiveTab(currentTab);
    }
  }, [searchParams]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
  };

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () =>
      (await api.get<{ data: ProductWithPlans }>(`/products/${productId}`)).data.data,
    enabled: !!productId,
  });

  const { data: subscriptions } = useQuery({
    queryKey: ['subscriptions', environment, client?.id],
    queryFn: async () => {
      const r = await api.get<{ success: boolean; data: Subscription[] }>(
        `/clients/${client?.id}/subscriptions`
      );
      return r.data.data.filter((sub) => sub.application_id === application.id);
    },
    enabled: !!client?.id && !!application.id,
  });

  const activeSubscription = subscriptions?.find(
    (s) => s.product_id === productId && s.status === 'active'
  );
  const hasSubscription = !!activeSubscription;

  const { data: appKeyData } = useQuery({
    queryKey: ['application-api-key', client?.id, application.id],
    queryFn: async () => {
      try {
        const res = await api.get<{
          success: boolean;
          data: { api_key?: string | null };
        }>(`/clients/${client?.id}/applications/${application.id}/api-key`);
        return res.data?.data?.api_key || null;
      } catch {
        return null;
      }
    },
    enabled: !!client?.id && !!application.id,
  });

  const testerApplications = useMemo(() => {
    return [
      {
        id: application.id,
        name: application.name,
        api_key: appKeyData || (application as any).api_key || application.api_key_prefix || '',
      },
    ];
  }, [application, appKeyData]);

  const downloadPostmanMutation = useMutation({
    mutationFn: async () =>
      (await api.get(`/products/${productId}/documentation/postman`, { responseType: 'blob' }))
        .data,
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `${product?.name}-postman-collection.json`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Coleção Postman descarregada com sucesso!');
    },
    onError: (err) => {
      toast.error(`Erro ao descarregar coleção Postman: ${getErrorMessage(err)}`);
    },
  });

  const handleDownloadOpenApi = () => {
    if (!product?.openapi_spec) {
      toast.error('Especificação OpenAPI não disponível para este produto.');
      return;
    }
    try {
      let specContent = '';
      if (typeof product.openapi_spec === 'object') {
        specContent = JSON.stringify(product.openapi_spec, null, 2);
      } else {
        const raw = String(product.openapi_spec).trim();
        if (raw.startsWith('{') || raw.startsWith('[')) {
          specContent = JSON.stringify(JSON.parse(raw), null, 2);
        } else {
          const parsed = yaml.load(raw);
          specContent = JSON.stringify(parsed, null, 2);
        }
      }

      const blob = new Blob([specContent], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute(
        'download',
        `${product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-openapi-spec.json`
      );
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Especificação OpenAPI 3.0 descarregada com sucesso!');
    } catch {
      toast.error('Erro ao exportar especificação OpenAPI.');
    }
  };

  const endpointsCount = useMemo<number>(() => {
    if (!product?.openapi_spec) return 0;
    try {
      const raw = product.openapi_spec as any;
      const spec =
        typeof raw === 'object'
          ? raw
          : String(raw).trim().startsWith('{')
            ? JSON.parse(raw)
            : yaml.load(raw);
      let n = 0;
      Object.values((spec as any).paths || {}).forEach((methods: any) => {
        if (methods && typeof methods === 'object')
          Object.keys(methods).forEach((m) => {
            if (
              ['get', 'post', 'put', 'patch', 'delete', 'options', 'head', 'trace'].includes(
                m.toLowerCase()
              )
            )
              n++;
          });
      });
      return n;
    } catch {
      return 0;
    }
  }, [product?.openapi_spec]);

  if (isLoading)
    return (
      <div className="py-8">
        <LoadingState type="card" count={1} />
      </div>
    );

  if (!product)
    return (
      <div className="py-12">
        <EmptyState
          title="Produto não encontrado"
          description="O produto não existe ou foi removido."
          action={
            <button
              onClick={() => navigate(`/applications/${application.id}/catalog`)}
              className="btn-base btn-primary btn-md"
            >
              Voltar ao Catálogo
            </button>
          }
        />
      </div>
    );

  return (
    <div className="animate-fade-in space-y-6">
      {/* Executive Product Header Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <button
              onClick={() => navigate(`/applications/${application.id}/catalog`)}
              className="text-slate-500 hover:text-primary-600 dark:text-slate-400 dark:hover:text-primary-400 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao Catálogo de APIs
            </button>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white m-0 tracking-tight">
                {product.name}
              </h1>
              {hasSubscription ? (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Subscrição Ativa
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                  Disponível para Subscrição
                </span>
              )}
            </div>
            <p className="text-slate-600 dark:text-slate-400 m-0 text-sm sm:text-base max-w-3xl leading-relaxed">
              {product.description}
            </p>

            {/* Metadata Badges */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                <ShieldCheck className="w-3.5 h-3.5 text-primary-500" />
                Autenticação: <strong>{product.authentication_provider || 'API_KEY'}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                <BookOpen className="w-3.5 h-3.5 text-indigo-500" />
                Endpoints: <strong>{endpointsCount} documentados</strong>
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                Versão: <strong>v{product.version || 1}</strong>
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0 md:justify-end">
            <button
              onClick={handleDownloadOpenApi}
              className="btn-base btn-secondary btn-sm border border-slate-200 dark:border-slate-700 flex items-center gap-2 shadow-xs hover:shadow"
              title="Descarregar Especificação OpenAPI 3.0 (JSON)"
            >
              <FileCode className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> OpenAPI 3.0
            </button>
            <button
              onClick={() => downloadPostmanMutation.mutate()}
              disabled={downloadPostmanMutation.isPending}
              className="btn-base btn-secondary btn-sm border border-slate-200 dark:border-slate-700 flex items-center gap-2 shadow-xs hover:shadow"
            >
              {downloadPostmanMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-primary-600" /> Baixando Coleção...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 text-primary-600 dark:text-primary-400" /> Coleção
                  Postman
                </>
              )}
            </button>
          </div>
        </div>

        {/* Clean Segmented Tabs inside Header Card */}
        <div className="border-t border-slate-100 dark:border-slate-800 pt-3 flex flex-wrap gap-2">
          {steps.map((step) => {
            const isActive = step.id === activeTab;
            return (
              <button
                key={step.id}
                onClick={() => handleTabChange(step.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-950/50 text-primary-700 dark:text-primary-300 shadow-xs border border-primary-200/80 dark:border-primary-800/80'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100/70 dark:hover:bg-slate-800/60'
                }`}
              >
                {step.icon}
                <span>{step.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Conteúdo por secção ───────────────────────────────────────────── */}

      {/* VISÃO GERAL */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary-500" /> Sobre esta API
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-sm">
                {product.description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center mb-3">
                  <Cpu className="w-5 h-5" />
                </div>
                <div className="text-3xl font-extrabold text-slate-900 dark:text-white">
                  {endpointsCount}
                </div>
                <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider">
                  Endpoints Disponíveis
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center mb-3">
                  <Layers className="w-5 h-5" />
                </div>
                <div className="text-3xl font-extrabold text-slate-900 dark:text-white">
                  {product.plans?.length || 0}
                </div>
                <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider">
                  Planos de Preços
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-3">
                  <KeyRound className="w-5 h-5" />
                </div>
                <div className="text-lg font-bold text-slate-900 dark:text-white pt-1">
                  {product.authentication_provider || 'API Key'}
                </div>
                <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-2 uppercase tracking-wider">
                  Mecanismo de Autenticação
                </div>
              </div>
            </div>

            {!hasSubscription ? (
              <div className="bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-950/40 dark:to-indigo-950/30 p-8 rounded-2xl border border-primary-200/80 dark:border-primary-800/60 shadow-sm">
                <h3 className="text-lg font-bold text-primary-900 dark:text-primary-300 mb-2 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary-600 dark:text-primary-400" />{' '}
                  Próximos Passos para Integração
                </h3>
                <p className="text-primary-800/80 dark:text-primary-300/80 text-sm mb-6 max-w-xl leading-relaxed">
                  Para emitir credenciais e efetuar chamadas a esta API através da aplicação{' '}
                  <strong className="text-primary-900 dark:text-primary-200">
                    {application.name}
                  </strong>
                  , selecione um dos planos disponíveis ou experimente requisições na Sandbox.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => handleTabChange('plans')}
                    className="btn-base btn-primary px-6 py-3 shadow-md hover:shadow-lg flex items-center gap-2"
                  >
                    Ver Planos e Subscrever <ArrowLeft className="w-4 h-4 ml-1 rotate-180" />
                  </button>
                  <button
                    onClick={() => handleTabChange('tester')}
                    className="btn-base btn-secondary px-5 py-3 border border-slate-300 dark:border-slate-700 flex items-center gap-2"
                  >
                    <FlaskConical className="w-4 h-4 text-indigo-500" />
                    <span>Testar na Sandbox</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/30 p-6 rounded-2xl border border-emerald-200/80 dark:border-emerald-800/60 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-bold text-emerald-900 dark:text-emerald-300 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />{' '}
                    Subscrição Pronta a Usar
                  </h3>
                  <p className="text-xs text-emerald-800/80 dark:text-emerald-300/80 mt-1 max-w-lg">
                    A sua aplicação tem acesso aprovado a esta API. Experimente fazer chamadas em tempo real na Consola de Testes.
                  </p>
                </div>
                <button
                  onClick={() => handleTabChange('tester')}
                  className="btn-base btn-primary btn-sm flex items-center gap-2 shadow-xs shrink-0"
                >
                  <FlaskConical className="w-4 h-4" />
                  <span>Abrir Consola de Testes</span>
                </button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
              <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4">
                Informações Técnicas
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                    Autenticação
                  </dt>
                  <dd>
                    <span className="bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-1 rounded-full text-xs font-semibold border border-slate-200 dark:border-slate-700 inline-block">
                      {product.authentication_provider || 'API Key'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                    Subscrição da Aplicação
                  </dt>
                  <dd>
                    {hasSubscription ? (
                      <span className="bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400 px-3 py-1 rounded-full text-xs font-bold flex items-center w-fit gap-1 border border-success-200 dark:border-success-800">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Ativa
                      </span>
                    ) : (
                      <span className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 px-3 py-1 rounded-full text-xs font-medium inline-block">
                        Não subscrito
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Gateway Headers Reference Card */}
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
              <div className="flex items-center gap-2">
                <Terminal className="w-5 h-5 text-indigo-500" />
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Cabeçalhos do Kong Gateway
                </h3>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                Cabeçalhos padrão enviados e devolvidos nas requisições HTTP através do Gateway.
              </p>

              <div className="space-y-3 pt-1">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-primary-600 dark:text-primary-400">
                      X-API-Key
                    </span>
                    <span className="text-[10px] font-bold uppercase text-slate-400">Request</span>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Chave secreta de autenticação da aplicação emitida pelo marketplace.
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-indigo-600 dark:text-indigo-400">
                      X-Application-ID
                    </span>
                    <span className="text-[10px] font-bold uppercase text-slate-400">Request</span>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Identificador UUID único da aplicação para validação de contexto e quotas.
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-emerald-600 dark:text-emerald-400">
                      X-Cache-Status
                    </span>
                    <span className="text-[10px] font-bold uppercase text-emerald-600 dark:text-emerald-400">
                      Response
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Status do cache em memória: <code>HIT</code> (&lt;5ms), <code>MISS</code> ou{' '}
                    <code>BYPASS</code>.
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-amber-600 dark:text-amber-400">
                      X-RateLimit-*
                    </span>
                    <span className="text-[10px] font-bold uppercase text-amber-600 dark:text-amber-400">
                      Response
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Limites do plano e chamadas restantes por janela de minuto/dia.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PLANOS */}
      {activeTab === 'plans' &&
        (!product.plans || product.plans.length === 0 ? (
          <EmptyState
            title="Nenhum plano disponível"
            description="Esta API ainda não possui planos configurados."
          />
        ) : (
          <div>
            {hasSubscription && (
              <div className="mb-8 p-6 rounded-2xl flex items-center justify-between gap-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 shadow-sm">
                <div className="flex items-center gap-4">
                  <CheckCircle2 className="w-8 h-8 text-success-600 dark:text-success-400 shrink-0" />
                  <div>
                    <p className="text-lg font-bold text-success-900 dark:text-success-300 mb-0.5">
                      Já tem uma subscrição ativa
                    </p>
                    <p className="text-success-700 dark:text-success-400 text-sm m-0">
                      A aplicação <strong>{application.name}</strong> está subscrita no plano{' '}
                      <strong>{activeSubscription?.plan?.tier}</strong>.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/applications/${application.id}/subscriptions`)}
                  className="btn-base bg-success-600 hover:bg-success-700 text-white px-5 py-2.5 shadow-sm text-sm font-semibold shrink-0"
                >
                  Gerir Subscrição
                </button>
              </div>
            )}

            {/* Simulator Header & Toggle */}
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-200 dark:border-slate-800">
              <div>
                <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-primary-500" />
                  <span>Simulador de Tráfego & Estimativa de Custos</span>
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Calcule o consumo mensal projetado e descubra o plano mais económico para a sua empresa.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowSimulator((prev) => !prev)}
                className={`btn-base btn-sm text-xs px-4 py-2 inline-flex items-center gap-1.5 transition-all ${
                  showSimulator
                    ? 'btn-secondary border-primary-500 text-primary-600'
                    : 'btn-primary'
                }`}
              >
                <Calculator className="w-4 h-4" />
                <span>{showSimulator ? 'Ocultar Simulador' : 'Abrir Simulador'}</span>
              </button>
            </div>

            {/* Expanded Simulator Card */}
            {showSimulator && (
              <div className="mb-8 p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-md">
                <PricingQuotaSimulator
                  productName={product.name}
                  plans={product.plans}
                  onSelectPlan={(planId) => {
                    navigate(
                      `/applications/${application.id}/subscriptions/new?product=${product.id}&plan=${planId}`
                    );
                  }}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {product.plans.map((plan) => {
                const isActivePlan =
                  activeSubscription?.plan_id === plan.id ||
                  activeSubscription?.plan?.id === plan.id ||
                  activeSubscription?.plan?.tier === plan.tier;

                const pricingModelInfo = {
                  monthly: {
                    label: 'Mensal',
                    icon: <Calendar className="w-3.5 h-3.5" />,
                    color:
                      'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
                  },
                  per_transaction: {
                    label: 'Por Transação',
                    icon: <DollarSign className="w-3.5 h-3.5" />,
                    color:
                      'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
                  },
                  hybrid: {
                    label: 'Híbrido',
                    icon: <Layers className="w-3.5 h-3.5" />,
                    color:
                      'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
                  },
                };

                const modelInfo =
                  pricingModelInfo[plan.pricing_model as keyof typeof pricingModelInfo] ||
                  pricingModelInfo.monthly;

                return (
                  <div
                    key={plan.id}
                    className={`bg-white dark:bg-slate-900 p-8 rounded-3xl flex flex-col relative border transition-all ${
                      isActivePlan
                        ? 'border-primary-500 dark:border-primary-500 ring-4 ring-primary-500/10 shadow-lg'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-lg'
                    }`}
                  >
                    {isActivePlan && (
                      <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2 z-10">
                        <span className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-xs font-bold bg-primary-600 text-white shadow-md border border-primary-500">
                          <Check className="w-3.5 h-3.5" /> Plano Ativo
                        </span>
                      </div>
                    )}

                    <div className="mb-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${modelInfo.color}`}
                      >
                        {modelInfo.icon}
                        {modelInfo.label}
                      </span>
                    </div>

                    <div className="mb-6 pb-6 border-b border-slate-100 dark:border-slate-800">
                      <h3 className="text-2xl font-bold text-slate-900 dark:text-white capitalize mb-4">
                        {plan.tier}
                      </h3>

                      {plan.pricing_model === 'monthly' && (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-extrabold text-slate-900 dark:text-white">
                              ${plan.monthly_price}
                            </span>
                            <span className="text-slate-500 dark:text-slate-400 font-medium">
                              /mês
                            </span>
                          </div>
                          <p className="text-slate-600 dark:text-slate-400 text-sm mt-2 font-medium">
                            {plan.monthly_quota.toLocaleString()} requisições incluídas
                          </p>
                        </>
                      )}

                      {plan.pricing_model === 'per_transaction' && (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-extrabold text-slate-900 dark:text-white">
                              ${plan.per_transaction_price}
                            </span>
                            <span className="text-slate-500 dark:text-slate-400 font-medium">
                              /req
                            </span>
                          </div>
                          <p className="text-slate-600 dark:text-slate-400 text-sm mt-2 font-medium">
                            Pay-as-you-go · Ilimitado
                          </p>
                        </>
                      )}

                      {plan.pricing_model === 'hybrid' && (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-extrabold text-slate-900 dark:text-white">
                              ${plan.monthly_price}
                            </span>
                            <span className="text-slate-500 dark:text-slate-400 font-medium">
                              /mês
                            </span>
                          </div>
                          <p className="text-slate-600 dark:text-slate-400 text-sm mt-2 font-medium">
                            {plan.monthly_quota.toLocaleString()} incl. + $
                            {plan.per_transaction_price}/req adicional
                          </p>
                        </>
                      )}
                    </div>

                    <div className="flex-1 space-y-3.5 mb-8">
                      {[
                        plan.pricing_model === 'monthly'
                          ? `${plan.monthly_quota.toLocaleString()} requisições/mês`
                          : plan.pricing_model === 'per_transaction'
                            ? 'Requisições ilimitadas'
                            : `${plan.monthly_quota.toLocaleString()} requisições incluídas`,
                        `${plan.rate_limit_per_minute} req/minuto limite`,
                        `Suporte ${plan.tier === 'enterprise' ? 'Prioritário' : 'Standard'}`,
                      ].map((f) => (
                        <div key={f} className="flex items-start gap-3 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                          <span className="text-slate-700 dark:text-slate-300 font-medium">
                            {f}
                          </span>
                        </div>
                      ))}
                    </div>

                    {!isActivePlan && (
                      <button
                        onClick={() =>
                          navigate(
                            `/applications/${application.id}/subscriptions/new?product=${product.id}&plan=${plan.id}`
                          )
                        }
                        className="btn-base btn-primary w-full py-3.5 text-base font-bold shadow-md hover:shadow-lg"
                      >
                        Subscrever Plano
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

      {/* DOCUMENTAÇÃO OPENAPI */}
      {activeTab === 'documentation' && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-6">
          <ApiDocumentation
            productId={product.id}
            openApiSpec={product.openapi_spec}
            applications={[application]}
            subscriptionId={activeSubscription?.id}
            authProvider={product.authentication_provider}
            productName={product.name}
          />
        </div>
      )}

      {/* CONSOLA DE TESTES (SANDBOX & LIVE) */}
      {activeTab === 'tester' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 mb-6 border-b border-slate-100 dark:border-slate-800">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-indigo-500" />
                  <span>Consola de Testes Interativa (Sandbox / Live)</span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Execute requisições em tempo real contra o Kong Gateway, simule cenários e visualize diagnósticos de cache e latência.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-800/80">
                  Aplicação: <strong>{application.name}</strong>
                </span>
              </div>
            </div>

            <ApiTester
              product={product as any}
              applications={testerApplications}
            />
          </div>
        </div>
      )}

      {/* VERSÕES & CHANGELOG */}
      {activeTab === 'changelog' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 sm:p-8 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-slate-100 dark:border-slate-800">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                    Versão Atual: v{product.version || 1}.0.0
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    Produção Estável
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
                  Histórico de Lançamentos & Ciclo de Vida da API
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-2xl leading-relaxed">
                  Todas as alterações nesta API são versionadas semanticamente. Versões anteriores mantêm retrocompatibilidade garantida por um período mínimo de 12 meses antes de depreciação.
                </p>
              </div>

              <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800 shrink-0">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">SLA de Disponibilidade</span>
                  <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">99.95% Garantido</span>
                </div>
                <div className="h-8 w-px bg-slate-200 dark:bg-slate-700" />
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">Política de Breaking Changes</span>
                  <span className="text-sm font-black text-slate-800 dark:text-slate-200">Aviso prévio 90 dias</span>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="space-y-8 pl-2 pt-2">
              <div className="relative pl-6 border-l-2 border-emerald-500 space-y-2">
                <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-500 ring-4 ring-emerald-100 dark:ring-emerald-950" />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-extrabold text-base text-slate-900 dark:text-white">v{product.version || 1}.0.0</span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">• Lançamento Mais Recente</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">Ativa</span>
                </div>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">
                  Versão de alta performance com suporte completo a autenticação via <strong>{product.authentication_provider || 'API Key'}</strong>, documentação OpenAPI 3.0 interativa, integração com Kong Proxy Cache para aceleração de respostas GET e geração multi-linguagem de snippets (cURL, JavaScript, Node, Python, PHP, Go, Java, C#).
                </p>
                <div className="pt-2 flex flex-wrap gap-2">
                  <span className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium">
                    + {endpointsCount} endpoints sincronizados
                  </span>
                  <span className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium">
                    + Suporte a Sandboxing & Mocking
                  </span>
                  <span className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium">
                    + Cache Kong X-Cache-Status
                  </span>
                </div>
              </div>

              <div className="relative pl-6 border-l-2 border-slate-200 dark:border-slate-800 space-y-2 opacity-75">
                <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-slate-400 dark:bg-slate-600" />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-sm text-slate-800 dark:text-slate-300">v0.9.0-rc</span>
                  <span className="text-xs text-slate-400">• Versão Prévia</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed max-w-3xl">
                  Validação de conformidade das especificações OpenAPI, definição de planos tarifários (Basic, Business, Enterprise) e configuração inicial de rate limits no Kong Gateway.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
